import { useAppDispatch, useAppSelector } from "../../../store";
import {
  BoardMachinesStates,
  boardStatesLoaded,
  clearBoard,
} from "../../../../../reducers/uploadedBoard";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import useTrackBoard, { useTrackBoardFailed } from "../boardTracking";
import {
  useRefreshBillOfMaterialsInStore,
  useSendBOMEvents,
} from "../updates/billOfMaterials";
import { useRefreshBoardInStore } from "../boardRefreshes";
import useRefreshQuote from "./quote";
import useRefreshPhysicalDetails from "./physicalDetails";
import { useBoardImages } from "../boardImages";
import useRefreshShipments from "./shipments";
import { useSendCustomerSpecificationsEvents } from "../updates/customerSpecifications";
import settings from "../../../../../constants/settings";
import useRefreshBoardRevision from "../updates/boardRevisions";
import useRefreshBoardFiles from "./boardFiles";
import useFlashPCBWebsocket, {
  FlashPCBWebsocketMessage,
} from "../../../../websocket";

const TERMINAL_CLOSE_REASONS = new Set(["board not found"]);

interface BoardMachinesStatesMessage extends FlashPCBWebsocketMessage {
  type: "board_machine_states";
  states: BoardMachinesStates;
}

function useGetLatestBoardStates(boardId: string) {
  const [latestBoardMachineStates, setLatestBoardMachineStates] =
    useState<BoardMachinesStates | null>(null);

  const onMessage = useCallback((message: BoardMachinesStatesMessage) => {
    setLatestBoardMachineStates(message.states);
  }, []);

  useFlashPCBWebsocket(
    `${settings.api_url}/boards/${boardId}/state-updates`,
    onMessage,
    TERMINAL_CLOSE_REASONS,
  );

  return latestBoardMachineStates;
}

function useBoardAndStates() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const isAdminOrEngineer = useAppSelector(
    (state) => state.auth.isAdminOrEngineer,
  );
  const boardIsLoaded = useAppSelector(
    (state) => !!state.uploadedBoard.boards[boardId]?.board,
  );
  const shipmentsAreLoaded = useAppSelector((state) => {
    const shipments = state.uploadedBoard.boards[boardId]?.shipments;
    return shipments !== undefined && Object.keys(shipments).length > 0;
  });
  const bomIsNull = useAppSelector(
    (state) => !state.uploadedBoard.boards[boardId]?.billOfMaterials,
  );
  const revisionsAreNull = useAppSelector(
    (state) => !state.uploadedBoard.boards[boardId]?.boardRevisions,
  );
  const states = useAppSelector(
    (state) => state.uploadedBoard.boards[boardId]?.states || null,
  );
  const errored = useAppSelector(
    (state) => state.uploadedBoard.boards[boardId]?.errored || false,
  );
  const notFound = useAppSelector(
    (state) => state.uploadedBoard.boards[boardId]?.notFound || false,
  );

  const { refreshBoard, handleBoardStateUpdates } = useRefreshBoardInStore();
  const { handleImageStateUpdates } = useBoardImages();
  const { refreshBillOfMaterials, handleBillOfMaterialsStateUpdates } =
    useRefreshBillOfMaterialsInStore();
  const { handlePhysicalDetailsStateUpdates } = useRefreshPhysicalDetails();
  const { handleQuoteStateUpdates } = useRefreshQuote();
  const { refreshShipments, handleShipmentsStateUpdates } =
    useRefreshShipments();
  const { handleBoardRevisionStateUpdates, refreshBoardRevision } =
    useRefreshBoardRevision();
  const { handleManufacturingOutputStateUpdates } = useRefreshBoardFiles();

  useEffect(() => {
    if (!errored && !notFound && !boardIsLoaded) {
      refreshBoard();
    }
  }, [boardIsLoaded, errored, notFound, refreshBoard]);

  useEffect(() => {
    if (!errored && !notFound && !shipmentsAreLoaded) {
      refreshShipments();
    }
  }, [errored, notFound, refreshShipments, shipmentsAreLoaded]);

  useEffect(() => {
    if (bomIsNull && !errored && !notFound) {
      refreshBillOfMaterials();
    }
  }, [bomIsNull, errored, notFound, refreshBillOfMaterials]);

  useEffect(() => {
    if (revisionsAreNull && !errored && !notFound && isAdminOrEngineer) {
      refreshBoardRevision();
    }
  }, [
    errored,
    isAdminOrEngineer,
    notFound,
    refreshBoardRevision,
    revisionsAreNull,
  ]);

  const nextStatesData = useGetLatestBoardStates(boardId);

  useEffect(() => {
    if (nextStatesData !== null && states !== null) {
      handleBoardStateUpdates(nextStatesData, states);
      handlePhysicalDetailsStateUpdates(nextStatesData, states);
      handleBillOfMaterialsStateUpdates(nextStatesData, states);
      handleQuoteStateUpdates(nextStatesData, states);
      handleShipmentsStateUpdates(nextStatesData, states);
      handleImageStateUpdates(nextStatesData, states);
      handleBoardRevisionStateUpdates(nextStatesData, states);
      handleManufacturingOutputStateUpdates(nextStatesData, states);
    }
    if (nextStatesData !== null) {
      dispatch(boardStatesLoaded({ boardId, states: nextStatesData }));
    }
  }, [
    boardId,
    dispatch,
    handleBillOfMaterialsStateUpdates,
    handleBoardStateUpdates,
    handleImageStateUpdates,
    handlePhysicalDetailsStateUpdates,
    handleQuoteStateUpdates,
    handleShipmentsStateUpdates,
    nextStatesData,
    states,
    handleBoardRevisionStateUpdates,
    handleManufacturingOutputStateUpdates,
  ]);
}

export interface BoardShipmentIds {
  boardId: string;
  shipmentId: string | null;
  setShipmentId?: (shipmentId: string) => void;
}

export const BoardIdContext = createContext<BoardShipmentIds | null>(null);

export function useLoadBoardForReviewBoardFlow() {
  // This should only exist once per board
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  useBoardAndStates();
  useSendBOMEvents();
  useSendCustomerSpecificationsEvents();

  useEffect(() => {
    return () => {
      if (boardId) {
        dispatch(clearBoard({ boardId }));
      }
    };
  }, [dispatch, boardId]);

  useTrackBoard(boardId);
  useTrackBoardFailed(boardId);
}
